import { useCallback, useState } from "react";
import {
  fetchUserBilling,
  grantTokens,
  replayWebhook,
  resyncEntitlement,
} from "../../api/admin-revenue";
import type { UserBillingData } from "../../api/admin-revenue";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { tableStyles } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import { LedgerTable, WebhookTable } from "./BillingTables";

type DialogKind = "grant" | "resync" | "replay" | null;

export function BillingPage(): React.JSX.Element {
  const [userId, setUserId] = useState("");
  const [billing, setBilling] = useState<UserBillingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [grantAmount, setGrantAmount] = useState("");
  const [replayTargetId, setReplayTargetId] = useState("");

  const handleSearch = useCallback(async (): Promise<void> => {
    if (!userId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      setBilling(await fetchUserBilling(userId.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const handleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      if (!billing) return;
      try {
        if (dialog === "grant") {
          await grantTokens(billing.user_id, Number(grantAmount), reason);
        } else if (dialog === "resync") {
          await resyncEntitlement(billing.user_id, reason);
        } else if (dialog === "replay") {
          await replayWebhook(replayTargetId, reason);
        }
        setDialog(null);
        setBilling(await fetchUserBilling(billing.user_id));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t("common.action_failed"),
        );
        setDialog(null);
      }
    },
    [dialog, billing, grantAmount, replayTargetId],
  );

  return (
    <div>
      <PageHeader
        title={t("billing.title")}
        description={t("billing.description")}
      />
      <SearchBar
        userId={userId}
        loading={loading}
        onChange={setUserId}
        onSearch={handleSearch}
      />
      {error && <p style={s.error}>{error}</p>}
      {billing && (
        <>
          <UserSummary
            billing={billing}
            onGrant={() => setDialog("grant")}
            onResync={() => setDialog("resync")}
          />
          {dialog === "grant" && (
            <GrantAmountInput value={grantAmount} onChange={setGrantAmount} />
          )}
          <LedgerTable entries={billing.token_ledger} />
          <WebhookTable
            events={billing.webhook_events}
            onReplay={(id) => {
              setReplayTargetId(id);
              setDialog("replay");
            }}
          />
        </>
      )}
      <ConfirmDialog
        open={dialog !== null}
        title={dialogTitle(dialog)}
        description={dialogDesc(dialog, grantAmount)}
        reasonRequired
        onConfirm={handleConfirm}
        onCancel={() => setDialog(null)}
      />
    </div>
  );
}

/* ---------- Sub-components ---------- */

function SearchBar({
  userId,
  loading,
  onChange,
  onSearch,
}: {
  userId: string;
  loading: boolean;
  onChange: (v: string) => void;
  onSearch: () => void;
}): React.JSX.Element {
  return (
    <div style={s.searchRow}>
      <input
        type="text"
        value={userId}
        placeholder={t("billing.search_placeholder")}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSearch()}
        style={s.searchInput}
      />
      <button
        type="button"
        onClick={onSearch}
        disabled={loading}
        style={s.searchBtn}
      >
        {loading ? t("common.loading") : t("common.search")}
      </button>
    </div>
  );
}

function UserSummary({
  billing,
  onGrant,
  onResync,
}: {
  billing: UserBillingData;
  onGrant: () => void;
  onResync: () => void;
}): React.JSX.Element {
  return (
    <div style={s.card}>
      <div style={s.cardGrid}>
        <Field
          label={t("billing.user")}
          value={`${billing.nickname} (${billing.email})`}
        />
        <Field label={t("billing.plan")} value={billing.plan} />
        <Field
          label={t("billing.status")}
          value={billing.subscription_status}
        />
        <Field
          label={t("billing.token_balance")}
          value={String(billing.token_balance)}
        />
      </div>
      <div style={s.cardActions}>
        <button type="button" onClick={onGrant} style={s.actionBtn}>
          {t("billing.grant_tokens")}
        </button>
        <button type="button" onClick={onResync} style={s.actionBtnDanger}>
          {t("billing.resync_entitlement")}
        </button>
      </div>
    </div>
  );
}

function GrantAmountInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div style={s.grantRow}>
      <label htmlFor="grant-amount" style={s.grantLabel}>
        {t("billing.token_amount_label")}
      </label>
      <input
        id="grant-amount"
        type="number"
        min={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={s.grantInput}
      />
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div>
      <span style={s.fieldLabel}>{label}</span>
      <span style={s.fieldValue}>{value}</span>
    </div>
  );
}

/* ---------- Helpers ---------- */

function dialogTitle(kind: DialogKind): string {
  if (kind === "grant") return t("billing.grant_title");
  if (kind === "resync") return t("billing.resync_title");
  if (kind === "replay") return t("billing.replay_title");
  return "";
}

function dialogDesc(kind: DialogKind, amount: string): string {
  if (kind === "grant")
    return t("billing.grant_desc", { amount: amount || "?" });
  if (kind === "resync") return t("billing.resync_desc");
  if (kind === "replay") return t("billing.replay_desc");
  return "";
}

/* ---------- Styles ---------- */

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  searchRow: { display: "flex", gap: 8, marginBottom: 24 },
  searchInput: {
    flex: 1,
    maxWidth: 360,
    padding: "8px 12px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 14,
  },
  searchBtn: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
  card: {
    padding: 16,
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    marginBottom: 24,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 12,
  },
  cardActions: { display: "flex", gap: 8 },
  actionBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
  actionBtnDanger: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-danger)",
    color: "#fff",
  },
  grantRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  grantLabel: { fontSize: 13, color: "var(--color-text-secondary)" },
  grantInput: {
    width: 120,
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 14,
  },
  fieldLabel: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    display: "block",
    marginBottom: 2,
  },
  fieldValue: { fontSize: 14, fontWeight: 500 },
};
